import { PROJECT_FORM_TYPES } from "../projectFormStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { getElementsFromDataFieldId } from "./dependencyUtils";
import { pvSystemController } from "./pvSystemElementController";
import { energyStorageSystemController } from "./energyStorageSystemElementController";
import { incentiveSelectorController } from "./incentiveSelectorElementController";
import { SUFFIXES } from "Forms/Components/Elements/_elementBase";
import { IS_DEBUG_BUILD, areCalculatedDecimalValuesEqual } from "Util/util";

export const E_SIGNATURE_OPTION = {
    None: 1,
    Required: 2,
    Optional: 3
};

export const NO_DATA = -1;
export const NO_DATA_STRING = "";
export const NO_DATA_DECIMAL = "-1.0";
export const INVALID_DATA = "-2";
export const INVALID_DATA_DECIMAL = "-2.0";

export function getValueOrDefault(value, defaultValue) {
    defaultValue = defaultValue || "";
    if (
        value &&
        value != NO_DATA &&
        value != NO_DATA_DECIMAL &&
        value != INVALID_DATA &&
        value != INVALID_DATA_DECIMAL
    ) {
        return value;
    }
    return defaultValue;
}
export function getForMultiValue(value) {
    if (value) {
        return value;
    }
    return NO_DATA;
}
export function getForStringMultiValue(value) {
    if (value) {
        return value;
    } else if (value === null) {
        return null;
    }
    return NO_DATA_STRING;
}
export function getEquipmentItemMultiValue(subsystems, propertyName) {
    return subsystems
        .map((s) => s.equipmentGroups.map((e) => getForMultiValue(e[propertyName])))
        .reduce((p, c) => p.concat(c));
}
export function sendMultiDataChange(element, suffix, value, dispatch) {
    const dataFieldId = element.GroupDataFields[suffix];
    dispatch(PROJECT_FORM_TYPES.actions.setProjectData, { dataFieldId, value }, { root: true });
}
export function mapMultiValues(element, fields, dataFieldMultiValueFromId) {
    const map = {};
    for (const name in fields) {
        if (fields.hasOwnProperty(name)) {
            const suffix = fields[name];
            if (element.GroupDataFields.hasOwnProperty(suffix)) {
                const id = element.GroupDataFields[suffix];
                map[name] = dataFieldMultiValueFromId(id);
            }
        }
    }
    return map;
}

function onSameAsOrStateDependencyUpdate({ element, dataFieldId, rootGetters, dispatch }) {
    const { formElement: dependencyFormElement } = getElementsFromDataFieldId(rootGetters, dataFieldId);

    // if the dependency update comes from THIS form element, then it means that the state value changed and
    // we should clear out the county value
    if (dependencyFormElement.PublicId === element.PublicId) {
        const ownCountyDataFieldId = element.GroupDataFields[SUFFIXES.Address.County];
        dispatch(
            PROJECT_FORM_TYPES.actions.setProjectData,
            { dataFieldId: ownCountyDataFieldId, value: null },
            { root: true }
        );
        return;
    }
    const getValue = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId];
    for (const suffix in element.GroupDataFields) {
        if (element.GroupDataFields.hasOwnProperty(suffix)) {
            const ownDataFieldId = element.GroupDataFields[suffix];
            const dependencyDataFieldId = dependencyFormElement.GroupDataFields[suffix];

            /**
             * It is possible that the dependency data field doesn't have all of the fields
             * defined (e.g., County), especially if it is really old.
             * Only update if we found the dependency.
             */
            if (dependencyDataFieldId) {
                const ownValue = getValue(ownDataFieldId);
                const dependencyValue = getValue(dependencyDataFieldId);
                if (ownValue !== dependencyValue) {
                    dispatch(
                        PROJECT_FORM_TYPES.actions.setProjectData,
                        { dataFieldId: ownDataFieldId, value: dependencyValue },
                        { root: true }
                    );
                }
            }
        }
    }
}

const elementControllers = {
    ContactElement: {
        onDependencyUpdate: onSameAsOrStateDependencyUpdate
    },
    AddressElement: {
        onDependencyUpdate: onSameAsOrStateDependencyUpdate
    },
    SummationElement: {
        onDependencyUpdate({ element, rootGetters, dispatch }) {
            const sum = element.DependencyDefinitions.reduce((total, dependencyDefinition) => {
                const fieldValStr = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](
                    dependencyDefinition.DataFieldId
                );
                return (Number(fieldValStr) || 0) + total;
            }, 0);
            const savedSum = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](element.DataFieldId);

            // Don't fire a setProjectData if the new value is the same as the saved one, of if saved sum is empty
            // and the calculated sum is 0
            const savedSumIsEmpty = Boolean(savedSum === "" || savedSum === null || savedSum === undefined);
            if (areCalculatedDecimalValuesEqual(sum, savedSum) || (sum === 0 && savedSumIsEmpty)) {
                return;
            }
            dispatch(
                PROJECT_FORM_TYPES.actions.setProjectData,
                { dataFieldId: element.DataFieldId, value: sum },
                { root: true }
            );
        }
    },
    PVSystemElement: pvSystemController,
    EnergyStorageSystemElement: energyStorageSystemController,
    IncentiveSelectorElement: incentiveSelectorController
};

export async function onDependencyUpdate({
    $app,
    baseController,
    element,
    projectElement,
    dataFieldId,
    rootGetters,
    dispatch,
    commit,
    state // Local state of the projectFormStore
}) {
    const elementController = elementControllers[element.ClassName];
    if (elementController && elementController.onDependencyUpdate) {
        return elementController.onDependencyUpdate({
            $app,
            baseController,
            element,
            projectElement,
            dataFieldId,
            rootGetters,
            dispatch,
            commit,
            state
        });
    }
}

export function getAuxProjectElementState(element, projectElement, Project) {
    const elementController = elementControllers[element.ClassName];
    if (elementController && elementController.getAuxProjectElementState) {
        return elementController.getAuxProjectElementState(element, projectElement, Project);
    }
}

export function doPvConfigUpdate(element, projectElement, pvConfig, systemIsNew, commit, dispatch) {
    if (element.ClassName !== "PVSystemElement") {
        if (IS_DEBUG_BUILD) {
            console.error(
                `Tried to update PV Configuration for a non-PVSystemElement. Actual className:  [${element.ClassName}]`
            );
        }
        return;
    }
    pvSystemController.commitPvConfig(element, pvConfig, systemIsNew, commit, dispatch);
}

export function doEnergyStorageConfigUpdate(element, projectElement, energyStorageConfig, commit, dispatch) {
    if (element.ClassName !== "EnergyStorageSystemElement") {
        if (IS_DEBUG_BUILD) {
            console.error(
                // eslint-disable-next-line max-len
                `Tried to update Energy Storage System Configuration for a non-EnergyStorageSystemElement. Actual className:  [${element.ClassName}]`
            );
        }
        return;
    }
    energyStorageSystemController.commitEnergyStorageConfig(element, energyStorageConfig, commit, dispatch);
}
